import {
  type AccessMode,
  AccessMode as AccessModeSchema,
  AgentKind,
  CodexApprovalsReviewer,
  ConsoleModelInfo,
  type EffectiveCli,
  Effort,
  effectiveCli,
  McpApproval,
  type RunMcpMode,
  RunMcpMode as RunMcpModeSchema,
} from "@minga/protocol"
import { Option, Schema } from "effect"
import { compareVersions } from "./format"

/**
 * Runner manifest, runner console contract v2 (runner → hub in `hello` and `manifest`, hub → web in
 * `GET /api/workspaces/:ws/runners`). The page decodes it piece by piece: a profile, tool or
 * connection this page can't read is dropped on its own, so one odd entry never hides the rest.
 * Manifests from v1 runners (profiles with `loggedIn`/`plan`, tools as "name@version" tokens) are
 * read too, so an older runner still shows up properly until it updates.
 */

const optional = <S extends Schema.Top>(schema: S) => Schema.optionalKey(Schema.NullOr(schema))

export const ToolInfo = Schema.Struct({
  name: Schema.String,
  version: optional(Schema.String),
  path: optional(Schema.String),
})

export const ConnectionKinds = ["gh", "git", "mcp", "xcode-signing", "ssh"] as const
export type ConnectionKind = (typeof ConnectionKinds)[number]
export const HealthStatuses = ["ok", "logged_out", "error"] as const

export const LocalConnection = Schema.Struct({
  id: Schema.String,
  // Kept open so a new kind from a newer runner still shows (with a generic icon).
  kind: Schema.String,
  label: Schema.String,
  status: Schema.Literals(HealthStatuses),
  detail: optional(Schema.String),
})

export const ModelInfo = Schema.Struct({
  id: Schema.String,
  label: optional(Schema.String),
  isDefault: optional(Schema.Boolean),
  reasoningEfforts: optional(Schema.Array(Schema.String)),
  contextWindow: optional(Schema.Number),
})

export const UsageWindow = Schema.Struct({
  id: Schema.String,
  label: Schema.String,
  usedPercent: Schema.Number,
  resetsAt: optional(Schema.String),
})

export const ProfileUsage = Schema.Struct({
  source: Schema.String,
  windows: Schema.Array(Schema.Unknown),
  updatedAt: Schema.String,
})

const ProfileV2 = Schema.Struct({
  id: Schema.String,
  agent: AgentKind,
  home: Schema.String,
  isDefault: optional(Schema.Boolean),
  enabled: optional(Schema.Boolean),
  capacity: optional(Schema.Number),
  cli: optional(Schema.Struct({ version: optional(Schema.String), path: optional(Schema.String) })),
  account: Schema.Struct({
    loggedIn: Schema.Boolean,
    plan: optional(Schema.String),
    accountLabel: optional(Schema.String),
    authMethod: optional(Schema.Literals(["subscription", "api_key", "unknown"])),
  }),
  models: optional(Schema.Array(Schema.Unknown)),
  defaultModel: optional(Schema.String),
  usage: optional(ProfileUsage),
  health: optional(
    Schema.Struct({
      status: Schema.Literals(["ok", "logged_out", "error", "unknown"]),
      message: optional(Schema.String),
    }),
  ),
  checkedAt: optional(Schema.String),
  /** Decoded on its own (parseCliSettings): an odd value there never hides the profile. */
  cliSettings: Schema.optionalKey(Schema.Unknown),
  /** Decision 0149/0159: a Platform Runner profile's sandbox (size, the name its admins gave it); read loosely. */
  container: Schema.optionalKey(Schema.Unknown),
})

/** v1 profile: `{ id, agent, home, loggedIn, plan? }`. */
const ProfileV1 = Schema.Struct({
  id: Schema.String,
  agent: AgentKind,
  home: Schema.String,
  loggedIn: Schema.Boolean,
  plan: optional(Schema.String),
})

const ManifestBase = Schema.Struct({
  runnerId: Schema.String,
  name: Schema.String,
  os: Schema.String,
  arch: Schema.String,
  version: Schema.String,
  autoUpdate: optional(Schema.Boolean),
  capacity: Schema.Number,
  capabilities: optional(Schema.Array(Schema.Unknown)),
  tools: optional(Schema.Array(Schema.Unknown)),
  localConnections: optional(Schema.Array(Schema.Unknown)),
  profiles: Schema.Array(Schema.Unknown),
  checkedAt: optional(Schema.String),
})

// ---------------------------------------------------------------------------------------------
// View model (what the page renders)
// ---------------------------------------------------------------------------------------------

export type Agent = AgentKind
export type HealthStatus = "ok" | "logged_out" | "error" | "unknown"

export interface Tool {
  readonly name: string
  readonly version: string | null
  readonly path: string | null
}

export interface Connection {
  readonly id: string
  readonly kind: string
  readonly label: string
  readonly status: (typeof HealthStatuses)[number]
  readonly detail: string | null
}

export interface Model {
  readonly id: string
  readonly label: string | null
  readonly isDefault: boolean
  readonly reasoningEfforts: ReadonlyArray<string>
  readonly contextWindow: number | null
  /** Claude (0.3.0): false = Auto falls back to edits only (S5); null = not reported. */
  readonly supportsAutoMode: boolean | null
}

export interface Usage {
  readonly source: string
  readonly windows: ReadonlyArray<{
    readonly id: string
    readonly label: string
    readonly usedPercent: number
    readonly resetsAt: string | null
  }>
  readonly updatedAt: string
}

/**
 * A profile's CLI settings as the runner reports them (`profiles[].cliSettings`, decision 0020, spec §7.3): the access
 * mode, the MCP mode, the four advanced overrides (null = from the access mode) and the effective values.
 */
export interface ProfileCli {
  /** null: the runner predates access modes ("Runner too old for access modes – update"). */
  readonly accessMode: AccessMode | null
  readonly mcp: RunMcpMode | null
  readonly claudePermissionMode: string | null
  readonly claudeSandbox: boolean | null
  readonly codexSandbox: string | null
  readonly codexApprovalPolicy: string | null
  /** What the CLI gets: reported by the runner, else derived from the mode and the overrides. null without a mode. */
  readonly effective: {
    readonly claudePermissionMode: string
    readonly codexSandbox: string
    readonly codexApprovalPolicy: string
  } | null
  /** Any of the three mode-driven fields is overridden in runner.toml. */
  readonly custom: boolean
  /** Settings wave (0.3.0 always reports them; null = an older runner, shown as "unknown"). */
  readonly mcpApproval: "approve" | "ask" | null
  readonly autoNetwork: boolean | null
  /** Minga's per-profile model; null = the CLI default (`Profile.defaultModel`). */
  readonly modelOverride: string | null
  readonly effortOverride: string | null
  /** The runner's own resolution of `config.toml` facts (spec §8.3): what runs really get. */
  readonly facts: {
    readonly mcpApproval: "approve" | "ask" | null
    readonly network: boolean | null
    readonly ownApproval: ReadonlyArray<string>
    /** Codex profiles, runner 0.6.1 (decision 0053): the reviewer its runs pass; null = not reported (older runner). */
    readonly codexApprovalsReviewer: "user" | "auto_review" | null
  }
}

export interface Profile {
  readonly id: string
  readonly agent: Agent
  readonly home: string
  readonly isDefault: boolean
  readonly enabled: boolean
  readonly capacity: number | null
  readonly cli: { readonly version: string | null; readonly path: string | null }
  readonly account: {
    readonly loggedIn: boolean
    readonly plan: string | null
    readonly accountLabel: string | null
    readonly authMethod: "subscription" | "api_key" | "unknown" | null
  }
  readonly models: ReadonlyArray<Model>
  readonly defaultModel: string | null
  readonly usage: Usage | null
  readonly health: { readonly status: HealthStatus; readonly message: string | null }
  readonly checkedAt: string | null
  /** null: not reported (an older runner). */
  readonly cliSettings: ProfileCli | null
  /** Decision 0159: the name its admins gave it (Platform Runner profiles), for its readable label. */
  readonly name?: string | null
  /** Decision 0159: the task sandbox's size ("standard-1", Platform Runner profiles). */
  readonly size?: string | null
}

export interface Manifest {
  readonly runnerId: string
  readonly name: string
  readonly os: string
  readonly arch: string
  readonly version: string
  readonly autoUpdate: boolean | null
  readonly capacity: number
  readonly capabilities: ReadonlyArray<string>
  readonly tools: ReadonlyArray<Tool>
  readonly localConnections: ReadonlyArray<Connection>
  readonly profiles: ReadonlyArray<Profile>
  readonly checkedAt: string | null
  /**
   * True for a v1 runner (before {@link CONSOLE_MIN_VERSION}): it ignores `config.update` and
   * `update`, has no `minga update`, and reports no models, usage or connections.
   */
  readonly legacy: boolean
}

// ---------------------------------------------------------------------------------------------
// Decoding
// ---------------------------------------------------------------------------------------------

const decode =
  <S extends Schema.ConstraintDecoder<unknown>>(schema: S) =>
  (value: unknown): S["Type"] | null =>
    Option.getOrNull(Schema.decodeUnknownOption(schema)(value))

const decodeBase = decode(ManifestBase)
const decodeProfileV2 = decode(ProfileV2)
const decodeProfileV1 = decode(ProfileV1)
const decodeTool = decode(ToolInfo)
const decodeConnection = decode(LocalConnection)
const decodeModel = decode(ModelInfo)
const decodeConsoleModel = decode(ConsoleModelInfo)
const decodeMcpApproval = Schema.decodeUnknownOption(McpApproval)
const decodeEffort = Schema.decodeUnknownOption(Effort)
const decodeReviewer = Schema.decodeUnknownOption(CodexApprovalsReviewer)
const decodeWindow = decode(UsageWindow)

const isPresent = <A>(value: A | null): value is A => value !== null

/** "xcode@26.0" → { name: "xcode", version: "26.0" }; a scoped "@scope/pkg@1" keeps its scope. */
export const parseToolToken = (token: string): Tool => {
  const at = token.lastIndexOf("@")
  if (at <= 0) return { name: token, version: null, path: null }
  return { name: token.slice(0, at), version: token.slice(at + 1) || null, path: null }
}

const toTool = (value: unknown): Tool | null => {
  if (typeof value === "string") return value ? parseToolToken(value) : null
  const tool = decodeTool(value)
  return tool ? { name: tool.name, version: tool.version ?? null, path: tool.path ?? null } : null
}

/** A model row: the protocol's `ConsoleModelInfo` first, else the lenient older shape (nulls allowed). */
const toModel = (value: unknown): Model | null => {
  const current = decodeConsoleModel(value)
  if (current)
    return {
      id: current.id,
      label: current.label ?? null,
      isDefault: current.isDefault ?? false,
      reasoningEfforts: current.reasoningEfforts ?? [],
      contextWindow: current.contextWindow ?? null,
      supportsAutoMode: current.supportsAutoMode ?? null,
    }
  const model = decodeModel(value)
  if (!model) return null
  return {
    id: model.id,
    label: model.label ?? null,
    isDefault: model.isDefault ?? false,
    reasoningEfforts: model.reasoningEfforts ?? [],
    contextWindow: model.contextWindow ?? null,
    supportsAutoMode: null,
  }
}

const nullableString = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null)
const decodeAccessMode = Schema.decodeUnknownOption(AccessModeSchema)
const decodeMcpMode = Schema.decodeUnknownOption(RunMcpModeSchema)

/** `profiles[].cliSettings`, field by field (all optional). */
export const parseCliSettings = (value: unknown): ProfileCli | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  const v = value as Record<string, unknown>
  const accessMode = Option.getOrNull(decodeAccessMode(v.accessMode))
  const mcp = Option.getOrNull(decodeMcpMode(v.mcp))
  const overrides = {
    claudePermissionMode: nullableString(v.claudePermissionMode),
    claudeSandbox: typeof v.claudeSandbox === "boolean" ? v.claudeSandbox : null,
    codexSandbox: nullableString(v.codexSandbox),
    codexApprovalPolicy: nullableString(v.codexApprovalPolicy),
  }
  const reported =
    v.effective && typeof v.effective === "object" ? (v.effective as Record<string, unknown>) : null
  let effective: ProfileCli["effective"] = null
  if (accessMode !== null) {
    const derived: EffectiveCli = effectiveCli({ ...overrides, accessMode, ...(mcp ? { mcp } : {}) } as never)
    effective = {
      claudePermissionMode: nullableString(reported?.claudePermissionMode) ?? derived.claudePermissionMode,
      codexSandbox: nullableString(reported?.codexSandbox) ?? derived.codexSandbox,
      codexApprovalPolicy: nullableString(reported?.codexApprovalPolicy) ?? derived.codexApprovalPolicy,
    }
  }
  const custom =
    typeof v.custom === "boolean"
      ? v.custom
      : accessMode !== null &&
        (overrides.claudePermissionMode !== null ||
          overrides.codexSandbox !== null ||
          overrides.codexApprovalPolicy !== null)
  const ownApproval = Array.isArray(reported?.ownApproval)
    ? reported.ownApproval.filter(
        (x): x is string => typeof x === "string" && x.length > 0 && x.length <= 128,
      )
    : []
  return {
    accessMode,
    mcp,
    ...overrides,
    effective,
    custom,
    mcpApproval: Option.getOrNull(decodeMcpApproval(v.mcpApproval)),
    autoNetwork: typeof v.autoNetwork === "boolean" ? v.autoNetwork : null,
    modelOverride:
      typeof v.modelOverride === "string" && v.modelOverride !== "" && v.modelOverride.length <= 128
        ? v.modelOverride
        : null,
    effortOverride: Option.getOrNull(decodeEffort(v.effortOverride)),
    facts: {
      mcpApproval: Option.getOrNull(decodeMcpApproval(reported?.mcpApproval)),
      network: typeof reported?.network === "boolean" ? reported.network : null,
      ownApproval,
      codexApprovalsReviewer: Option.getOrNull(decodeReviewer(reported?.codexApprovalsReviewer)),
    },
  }
}

/**
 * How one MCP server reads on the runner page (wave O1, RF3). A plugin's server comes with `detail` "plugin <plugin> ·
 * <transport> · <profile>" and the name `<server>@<plugin>` (Claude Code): "cloudflare (plugin cloudflare)". A Codex
 * plugin comes as its id with `detail` "plugin · <profile>" (its servers are unknown): "<id> (plugin)". Anything else
 * (a user server, `detail` "<transport> · <profile>") is its name. Names only: never a URL, command or argument.
 */
export const mcpServerLabel = (name: string, detail: string | null | undefined): string => {
  const first = (detail ?? "").split(" · ")[0]?.trim() ?? ""
  if (first === "plugin") return `${name} (plugin)`
  const plugin = /^plugin\s+(\S.*)$/.exec(first)?.[1]?.trim()
  if (!plugin) return name
  const suffix = `@${plugin}`
  const server = name.endsWith(suffix) && name.length > suffix.length ? name.slice(0, -suffix.length) : name
  return `${server} (plugin ${plugin})`
}

const isPluginServer = (detail: string | null | undefined): boolean =>
  /^plugin(\s|$)/.test((detail ?? "").trim())

/**
 * The MCP servers a profile's runs load, by name: `localConnections` of kind `mcp` with id `mcp:<profileId>:<name>`
 * (the label when the id has another shape). The person's own servers first, then the ones plugins bring, each as
 * `mcpServerLabel` reads it.
 */
export const mcpServersOf = (
  manifest: Pick<Manifest, "localConnections">,
  profileId: string,
): ReadonlyArray<string> => {
  const prefix = `mcp:${profileId}:`
  const servers = manifest.localConnections.filter((c) => c.kind === "mcp" && c.id.startsWith(prefix))
  const label = (c: (typeof servers)[number]) =>
    mcpServerLabel(c.id.slice(prefix.length) || c.label, c.detail)
  return [
    ...servers.filter((c) => !isPluginServer(c.detail)).map(label),
    ...servers.filter((c) => isPluginServer(c.detail)).map(label),
  ]
}

const clampPercent = (value: number) => (Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0)

/** A Platform Runner profile's `container` ({ size, name }), field by field; nothing when absent or odd. */
const containerFacts = (value: unknown): { readonly name?: string; readonly size?: string } => {
  if (!value || typeof value !== "object") return {}
  const c = value as Record<string, unknown>
  return {
    ...(typeof c.name === "string" && c.name.trim() ? { name: c.name.trim() } : {}),
    ...(typeof c.size === "string" && c.size.trim() ? { size: c.size.trim() } : {}),
  }
}

const toProfile = (value: unknown): Profile | null => {
  const v2 = decodeProfileV2(value)
  if (v2) {
    const loggedIn = v2.account.loggedIn
    return {
      id: v2.id,
      agent: v2.agent,
      home: v2.home,
      isDefault: v2.isDefault ?? false,
      enabled: v2.enabled ?? true,
      capacity: v2.capacity ?? null,
      cli: { version: v2.cli?.version ?? null, path: v2.cli?.path ?? null },
      account: {
        loggedIn,
        plan: v2.account.plan ?? null,
        accountLabel: v2.account.accountLabel ?? null,
        authMethod: v2.account.authMethod ?? null,
      },
      models: (v2.models ?? []).map(toModel).filter(isPresent),
      defaultModel: v2.defaultModel ?? null,
      usage: v2.usage
        ? {
            source: v2.usage.source,
            updatedAt: v2.usage.updatedAt,
            windows: v2.usage.windows
              .map(decodeWindow)
              .filter(isPresent)
              .map((w) => ({
                id: w.id,
                label: w.label,
                usedPercent: clampPercent(w.usedPercent),
                resetsAt: w.resetsAt ?? null,
              })),
          }
        : null,
      health: {
        status: v2.health?.status ?? (loggedIn ? "unknown" : "logged_out"),
        message: v2.health?.message ?? null,
      },
      checkedAt: v2.checkedAt ?? null,
      cliSettings: parseCliSettings(v2.cliSettings),
      ...containerFacts(v2.container),
    }
  }
  const v1 = decodeProfileV1(value)
  if (!v1) return null
  return {
    id: v1.id,
    agent: v1.agent,
    home: v1.home,
    isDefault: false,
    enabled: true,
    capacity: null,
    cli: { version: null, path: null },
    account: { loggedIn: v1.loggedIn, plan: v1.plan ?? null, accountLabel: null, authMethod: null },
    models: [],
    defaultModel: null,
    usage: null,
    health: { status: v1.loggedIn ? "ok" : "logged_out", message: null },
    checkedAt: null,
    cliSettings: null,
  }
}

/**
 * The first runner version that speaks console v2 (settings, refresh, self-update). Detected by
 * version, not by missing fields: the hub fills `capabilities`/`localConnections` in for v1 runners.
 */
export const CONSOLE_MIN_VERSION = "0.1.0"

/** Reads a v2 (or v1) manifest. Returns null only when the basics (id, name, platform, capacity) are missing. */
export const parseManifest = (value: unknown): Manifest | null => {
  const base = decodeBase(value)
  if (!base) return null
  return {
    runnerId: base.runnerId,
    name: base.name,
    os: base.os,
    arch: base.arch,
    version: base.version,
    autoUpdate: base.autoUpdate ?? null,
    capacity: base.capacity,
    capabilities: (base.capabilities ?? []).filter((c): c is string => typeof c === "string" && c !== ""),
    tools: (base.tools ?? []).map(toTool).filter(isPresent),
    localConnections: (base.localConnections ?? [])
      .map(decodeConnection)
      .filter(isPresent)
      .map((c) => ({
        id: c.id,
        kind: c.kind,
        label: c.label,
        status: c.status,
        detail: c.detail ?? null,
      })),
    profiles: base.profiles.map(toProfile).filter(isPresent),
    checkedAt: base.checkedAt ?? null,
    legacy:
      compareVersions(base.version, CONSOLE_MIN_VERSION) < 0 ||
      (base.capabilities === undefined && base.localConnections === undefined),
  }
}
